import { Bell } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { Card } from "@/components/ui/card";
import { useNotifications } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function NotificationsPage() {
  const { data: notifications, isLoading, isError, error, refetch } = useNotifications();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Notifications</h1>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {["a", "b", "c", "d", "e"].map((k) => (
            <div key={k} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState message={error?.message ?? "Failed to load notifications"} onRetry={refetch} />
      )}

      {!isLoading && !isError && notifications?.length === 0 && (
        <Card className="flex flex-col items-center gap-3 py-16 text-center">
          <Bell className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">You have no notifications.</p>
        </Card>
      )}

      {!isLoading && !isError && notifications && notifications.length > 0 && (
        <div className="space-y-2">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={cn(
                "flex items-start gap-3 rounded-lg border p-4 transition-colors",
                n.read ? "bg-background" : "bg-primary/5 border-primary/20",
              )}
            >
              <span
                className={cn(
                  "mt-1 h-2 w-2 shrink-0 rounded-full",
                  n.read ? "bg-transparent" : "bg-primary",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4">
                  <p className={cn("text-sm font-medium", !n.read && "font-semibold")}>
                    {n.title ?? "Notification"}
                  </p>
                  {n.date && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatDate(n.date)}
                    </span>
                  )}
                </div>
                {n.message && <p className="mt-0.5 text-sm text-muted-foreground">{n.message}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
